import { CSSProperties, ReactNode, InputHTMLAttributes } from 'react';
import { useTheme } from '../context/ThemeContext';
import Icon from './Icon';

export function PageHeader({ icon, title, right }: { icon: string; title: string; right?: ReactNode }) {
  const { colors } = useTheme();
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '16px 20px',
        paddingTop: 'calc(16px + env(safe-area-inset-top, 0px))',
        backgroundColor: colors.header,
        borderBottom: `1px solid ${colors.headerBorder}`,
        position: 'sticky', top: 0, zIndex: 10,
      }}
    >
      <Icon name={icon} size={22} color={colors.accentDark} />
      <div style={{ fontSize: 20, fontWeight: 800, color: colors.text, flex: 1 }}>{title}</div>
      {right}
    </div>
  );
}

export function SectionLabel({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  const { colors } = useTheme();
  return (
    <div style={{
      fontSize: 13, fontWeight: 700, color: colors.textSub,
      marginTop: 18, marginBottom: 10, letterSpacing: 0.2,
      ...style,
    }}>
      {children}
    </div>
  );
}

interface MoneyInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  decimal?: boolean;
}

export function MoneyInput({ value, onChange, placeholder, decimal = true }: MoneyInputProps) {
  const { colors } = useTheme();
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center',
        backgroundColor: colors.input, border: `1px solid ${colors.inputBorder}`,
        borderRadius: 10, padding: '0 14px', minHeight: 50,
      }}
    >
      <Icon name="dollar-sign" size={15} color={colors.textSub} style={{ marginRight: 10 }} />
      <input
        type="text"
        inputMode={decimal ? 'decimal' : 'numeric'}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          flex: 1, padding: '14px 0',
          fontSize: 15, color: colors.inputText,
          background: 'transparent', border: 'none', outline: 'none',
        }}
      />
    </div>
  );
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  icon?: string;
  value: string;
  onChange: (v: string) => void;
}

export function TextField({ icon, value, onChange, style, ...rest }: TextFieldProps) {
  const { colors } = useTheme();
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center',
        backgroundColor: colors.input, border: `1px solid ${colors.inputBorder}`,
        borderRadius: 10, padding: '0 14px', minHeight: 50,
        ...style,
      }}
    >
      {icon && <Icon name={icon} size={14} color={colors.textSub} style={{ marginRight: 10 }} />}
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        {...rest}
        style={{
          flex: 1, padding: '14px 0',
          fontSize: 15, color: colors.inputText,
          background: 'transparent', border: 'none', outline: 'none', width: '100%',
        }}
      />
    </div>
  );
}

interface DropdownProps {
  icon: string;
  text: string;
  placeholder: string;
  onClick: () => void;
}

export function Dropdown({ icon, text, placeholder, onClick }: DropdownProps) {
  const { colors } = useTheme();
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        backgroundColor: colors.input, border: `1px solid ${colors.inputBorder}`,
        borderRadius: 12, padding: 14, minHeight: 50,
        cursor: 'pointer', textAlign: 'left',
      }}
    >
      <Icon name={icon} size={15} color={colors.textSub} />
      <div style={{ flex: 1, fontSize: 15, fontWeight: 500, color: text ? colors.inputText : colors.textMuted }}>
        {text || placeholder}
      </div>
      <Icon name="chevron-down" size={13} color={colors.textMuted} />
    </button>
  );
}

interface ChipProps {
  label: ReactNode;
  active: boolean;
  onClick: () => void;
  icon?: ReactNode;
  width?: string;
}

export function Chip({ label, active, onClick, icon, width }: ChipProps) {
  const { colors } = useTheme();
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '10px 14px', borderRadius: 999,
        border: `1.5px solid ${active ? colors.accent : colors.cardBorder}`,
        backgroundColor: active ? colors.accent : colors.card,
        color: active ? '#020617' : colors.text,
        fontSize: 13, fontWeight: 700,
        minHeight: 44, cursor: 'pointer',
        width,
      }}
    >
      {icon}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    </button>
  );
}

export function AmountChip({ amount, active, onClick }: { amount: number; active: boolean; onClick: () => void }) {
  const { colors } = useTheme();
  return (
    <button
      onClick={onClick}
      style={{
        flex: '0 1 calc(33.333% - 7px)',
        padding: '14px 0', borderRadius: 999,
        border: `1.5px solid ${active ? colors.accent : colors.cardBorder}`,
        backgroundColor: active ? colors.accent : colors.card,
        color: active ? '#020617' : colors.text,
        fontSize: 15, fontWeight: 700,
        minHeight: 50, cursor: 'pointer',
      }}
    >
      ${amount}
    </button>
  );
}

export function SummaryCard({ title, children }: { title: string; children: ReactNode }) {
  const { colors } = useTheme();
  return (
    <div
      style={{
        backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
        borderRadius: 14, padding: 16, marginTop: 22,
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 800, color: colors.text, marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

export function SummaryRow({ label, value }: { label: string; value: ReactNode }) {
  const { colors } = useTheme();
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', gap: 12 }}>
      <div style={{ fontSize: 13, color: colors.textSub }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: colors.text, textAlign: 'right' }}>{value}</div>
    </div>
  );
}

interface PayButtonProps {
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  icon?: string;
}

export function PayButton({ label, onClick, loading, disabled, icon = 'arrow-right' }: PayButtonProps) {
  const { colors } = useTheme();
  const isDisabled = disabled || loading;
  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      style={{
        width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8,
        borderRadius: 999, padding: '16px 0', marginTop: 24,
        border: 'none',
        backgroundColor: isDisabled ? '#94a3b8' : colors.accent,
        color: '#020617', fontSize: 15, fontWeight: 800,
        minHeight: 50,
        cursor: isDisabled ? 'default' : 'pointer',
      }}
    >
      {loading ? (
        <span style={{
          width: 18, height: 18, border: '2px solid #020617', borderTopColor: 'transparent',
          borderRadius: '50%', animation: 'aga-spin 0.7s linear infinite',
        }} />
      ) : (
        <>
          <span>{label}</span>
          <Icon name={icon} size={14} color="#020617" />
        </>
      )}
    </button>
  );
}

export function PleaseWait({ open, title, message }: { open: boolean; title: string; message: string }) {
  const { colors } = useTheme();
  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 32, zIndex: 9500,
      }}
    >
      <div
        style={{
          backgroundColor: colors.card, borderRadius: 20, padding: 32,
          width: '100%', maxWidth: 420, textAlign: 'center',
        }}
      >
        <div style={{
          width: 48, height: 48, margin: '0 auto',
          border: `5px solid ${colors.accentSoft}`, borderTopColor: colors.accentDark,
          borderRadius: '50%', animation: 'aga-spin 0.7s linear infinite',
        }} />
        <div style={{ fontSize: 18, fontWeight: 800, color: colors.text, marginTop: 16 }}>{title}</div>
        <div style={{ fontSize: 14, color: colors.textSub, lineHeight: 1.5, marginTop: 8 }}>{message}</div>
      </div>
    </div>
  );
}
